import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import type { AudienceSummary, LeadList } from '@/api/types';
import {
  CAMPAIGN_QUICK_DELIVERY_LABELS,
  CAMPAIGN_SEND_RAIL_LABELS,
  type CampaignQuickDelivery,
} from '@marketing-hub/shared';
import { useFeatureFlags } from '@/features/platform/useFeatureFlags';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { useToast } from '@/components/toast/ToastProvider';
import campStyles from './Campaigns.module.css';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from '@/pages/ProjectsPage.module.css';

type TemplateOption = { id: string; name: string; subject_template: string };

type Props = {
  projectId: string;
  /** Pre-select template (bibliotheek) */
  defaultTemplateId?: string;
  compact?: boolean;
};

export function CampaignQuickSendPanel({ projectId, defaultTemplateId, compact }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { isEnabled } = useFeatureFlags();
  const audiencesOn = isEnabled('audiences_v1');

  const [templateId, setTemplateId] = useState(defaultTemplateId ?? '');
  const [listId, setListId] = useState('');
  const [audienceId, setAudienceId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [delivery, setDelivery] = useState<CampaignQuickDelivery>('brevo_broadcast');
  const [sendRail, setSendRail] = useState<'smtp' | 'brevo_transactional'>('smtp');

  useEffect(() => {
    if (defaultTemplateId) setTemplateId(defaultTemplateId);
  }, [defaultTemplateId]);

  const templatesQ = useQuery({
    queryKey: ['email-templates', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ templates: TemplateOption[] }>(`/api/email-templates?project_id=${projectId}`),
  });

  const listsQ = useQuery({
    queryKey: ['lists', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ lists: LeadList[] }>(`/api/lists?project_id=${projectId}`),
  });

  const audiencesQ = useQuery({
    queryKey: ['audiences', projectId],
    enabled: Boolean(projectId) && audiencesOn,
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(`/api/audiences?project_id=${projectId}`),
  });

  const integrationsQ = useQuery({
    queryKey: ['integrations', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ integrations: { connector_type: string; status: string }[] }>(
        `/api/integrations?project_id=${projectId}`,
      ),
  });

  const hasBrevo = Boolean(
    integrationsQ.data?.integrations.some((i) => i.connector_type === 'brevo' && i.status === 'active'),
  );

  useEffect(() => {
    if (!hasBrevo && delivery === 'brevo_broadcast') {
      setDelivery('sequence');
    }
  }, [hasBrevo, delivery]);

  const quickSend = useMutation({
    mutationFn: () =>
      apiFetch<{
        campaign: { id: string; name: string };
        enrolled?: number;
        contact_count?: number;
      }>('/api/campaigns/quick-send', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          email_template_id: templateId,
          ...(audienceId ? { audience_id: audienceId } : { list_id: listId }),
          ...(campaignName.trim() ? { name: campaignName.trim() } : {}),
          delivery,
          ...(delivery === 'sequence' ? { send_rail: sendRail } : {}),
        }),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
      const msg =
        delivery === 'brevo_broadcast'
          ? `Broadcast verstuurd — ${data.contact_count ?? '?'} contacten`
          : `Sequence gestart — ${data.enrolled ?? 0} enrolled`;
      toast.push(msg, 'success');
      navigate(`/app/campagnes/${data.campaign.id}`);
    },
    onError: (e: Error) => {
      if (
        e instanceof ApiError &&
        (e.code === 'MAX_RUNNING_CAMPAIGNS' || e.code === 'OUTREACH_NOT_READY')
      ) {
        const cid = (e.details as { campaign_id?: string })?.campaign_id;
        toast.push(e.message, 'error');
        if (cid) navigate(`/app/campagnes/${cid}`);
        return;
      }
      toast.push(e.message, 'error');
    },
  });

  const templates = templatesQ.data?.templates ?? [];
  const canSubmit =
    Boolean(templateId) &&
    Boolean(audienceId || listId) &&
    (delivery !== 'brevo_broadcast' || hasBrevo);

  const selectedTpl = templates.find((t) => t.id === templateId);

  return (
    <section className={campStyles.panel}>
      <div className={campStyles.panelHead}>
        <h2 className={campStyles.panelTitle}>Direct versturen</h2>
        {!compact ? (
          <span className={campStyles.metaChip}>Template → audience → verzenden</span>
        ) : null}
      </div>
      <div className={campStyles.panelBody}>
        <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
          Kies een template en doelgroep. Hub maakt automatisch een sequence (1 stap) en campagne aan
          {delivery === 'brevo_broadcast' ? ' en verstuurt via Brevo.' : ' en start outreach.'}
        </p>

        <form
          className={styles.formGrid}
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (!canSubmit) return;
            quickSend.mutate();
          }}
        >
          <Field label="E-mailtemplate">
            <select
              className={leadStyles.filterInput}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              required
            >
              <option value="">— kies template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {selectedTpl ? (
            <p className={styles.mutedSmall}>Onderwerp: {selectedTpl.subject_template.slice(0, 120)}</p>
          ) : null}

          {audiencesOn ? (
            <Field label="Doelgroep">
              <select
                className={leadStyles.filterInput}
                value={audienceId}
                onChange={(e) => {
                  setAudienceId(e.target.value);
                  if (e.target.value) setListId('');
                }}
              >
                <option value="">— optioneel: doelgroep —</option>
                {(audiencesQ.data?.audiences ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.lead_count})
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <Field label={audiencesOn ? 'Of: lijst' : 'Lijst'}>
            <select
              className={leadStyles.filterInput}
              value={listId}
              onChange={(e) => {
                setListId(e.target.value);
                if (e.target.value) setAudienceId('');
              }}
              disabled={Boolean(audienceId)}
              required={!audienceId}
            >
              <option value="">— kies lijst —</option>
              {(listsQ.data?.lists ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.member_count})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Campagnenaam (optioneel)">
            <TextInput
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Leeg = template + datum"
            />
          </Field>

          <Field label="Verzendwijze">
            <select
              className={leadStyles.filterInput}
              value={delivery}
              onChange={(e) => setDelivery(e.target.value as CampaignQuickDelivery)}
            >
              {(Object.keys(CAMPAIGN_QUICK_DELIVERY_LABELS) as CampaignQuickDelivery[]).map((k) => (
                <option key={k} value={k} disabled={k === 'brevo_broadcast' && !hasBrevo}>
                  {CAMPAIGN_QUICK_DELIVERY_LABELS[k]}
                  {k === 'brevo_broadcast' && !hasBrevo ? ' (Brevo niet gekoppeld)' : ''}
                </option>
              ))}
            </select>
          </Field>

          {delivery === 'sequence' ? (
            <Field label="Verzendrail">
              <select
                className={leadStyles.filterInput}
                value={sendRail}
                onChange={(e) => setSendRail(e.target.value as 'smtp' | 'brevo_transactional')}
              >
                <option value="smtp">{CAMPAIGN_SEND_RAIL_LABELS.smtp}</option>
                <option value="brevo_transactional">
                  {CAMPAIGN_SEND_RAIL_LABELS.brevo_transactional}
                </option>
              </select>
            </Field>
          ) : null}

          <div className={campStyles.commandActions}>
            <Button type="submit" disabled={!canSubmit || quickSend.isPending}>
              {quickSend.isPending ? 'Bezig…' : 'Nu versturen'}
            </Button>
            <Link to="/app/campagnes/bibliotheek">
              <Button type="button" variant="ghost">
                Templates beheren
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
